// JCR-HISTORY-001 — one card per jurisdiction; correction requests roll up into a collapsible history
// (UI/navigation only: records, ids, deep links and the approval workflow are untouched).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { History, CheckCircle2, XCircle, Clock, ChevronDown, ChevronRight, ArrowRight } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet';
import { formatDateOnlyET, formatDateTimeET, displayTitleCase } from '../../utils/formatters';
import { CORRECTION_ID, FIELD_LABEL, RequestJurisdictionCorrectionDialog } from './RequestJurisdictionCorrectionDialog';
import { CorrectionDetailsLiteDialog } from './RequestMissingJurisdictionDialog';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
export const JCR_CHANGED_EVENT = 'jcr:changed';

const cache = new Map();
const fetchCorrections = (orderId, force) => {
  if (force || !cache.has(orderId)) {
    cache.set(orderId, axios.get(`${API}/api/jurisdiction-correction-requests`, { params: { order_id: orderId }, ...auth() })
      .then((r) => r.data?.items || []).catch(() => []));
  }
  return cache.get(orderId);
};

/** One shared read of this order's correction requests; re-reads on focus return (throttled) and on JCR_CHANGED_EVENT. */
export const useOrderCorrections = (orderId) => {
  const [items, setItems] = useState([]);
  const last = useRef(0);
  const refresh = useCallback((force = true) => {
    if (!orderId) return Promise.resolve([]);
    last.current = Date.now();
    return fetchCorrections(orderId, force).then((list) => { setItems(list); return list; });
  }, [orderId]);
  useEffect(() => { refresh(true); }, [refresh]);
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible' && Date.now() - last.current > 15000) refresh(true); };
    const onChanged = () => refresh(true);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    window.addEventListener(JCR_CHANGED_EVENT, onChanged);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      window.removeEventListener(JCR_CHANGED_EVENT, onChanged);
    };
  }, [refresh]);
  return { corrections: items, refresh };
};

const norm = (s) => (s || '').trim().toUpperCase();
const refOf = (c) => c.jurisdiction_ref || c.payload || {};
const matches = (c, j) => {
  if (j?.jurisdiction_id && c.jurisdiction_id) return j.jurisdiction_id === c.jurisdiction_id;
  const r = refOf(c);
  return !!j?.jurisdiction_name && norm(r.jurisdiction_name) === norm(j.jurisdiction_name) && norm(r.jurisdiction_type) === norm(j.jurisdiction_type);
};

/** Newest-first; `byIndex` keys are parcel-jurisdiction indexes, `unmatched` keeps requests whose jurisdiction is not on this parcel. */
export const groupCorrectionsByJurisdiction = (corrections, jurisdictions) => {
  const byIndex = new Map();
  const unmatched = [];
  const sorted = [...(corrections || [])].sort((a, b) => (b.submitted_at || '').localeCompare(a.submitted_at || ''));
  for (const c of sorted) {
    const idx = (jurisdictions || []).findIndex((j) => matches(c, j));
    if (idx < 0) { unmatched.push(c); continue; }
    if (!byIndex.has(idx)) byIndex.set(idx, []);
    byIndex.get(idx).push(c);
  }
  return { byIndex, unmatched };
};

const VERB = { approved: 'updated', pending: 'change requested', rejected: 'change rejected' };
export const correctionSummary = (c) => {
  const labels = (c.changes || []).map((ch) => FIELD_LABEL[ch.field] || ch.field);
  if (!labels.length) return c.reason || 'Correction request';
  const more = labels.length - 2;
  return `${labels.slice(0, 2).join(', ')}${more > 0 ? ` +${more} more` : ''} ${VERB[c.status] || VERB.pending}`;
};

const STATUS = {
  pending: { label: 'Pending', Icon: Clock, icon: 'text-amber-500', pill: 'bg-amber-50 text-amber-800 ring-amber-200' },
  approved: { label: 'Approved', Icon: CheckCircle2, icon: 'text-emerald-600', pill: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  rejected: { label: 'Rejected', Icon: XCircle, icon: 'text-rose-500', pill: 'bg-rose-50 text-rose-700 ring-rose-200' },
};
const plural = (n) => `${n} request${n !== 1 ? 's' : ''}`;

const CorrectionHistoryPanel = ({ open, onClose, items, jurisdiction, onView }) => (
  <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
    <SheetContent side="right" className="w-[400px] sm:max-w-[400px] p-0 flex flex-col rounded-l-2xl" overlayClassName="bg-slate-900/20" data-testid="jcr-history-panel">
      <SheetHeader className="px-5 pt-5 pb-4 border-b border-slate-100 text-left space-y-1">
        <SheetTitle className="text-[18px] font-bold text-slate-900">Correction History</SheetTitle>
        <SheetDescription className="text-[13px] text-slate-500" data-testid="jcr-history-panel-jurisdiction">{displayTitleCase(jurisdiction?.jurisdiction_name) || 'Jurisdiction'}</SheetDescription>
        <span className="inline-flex w-fit rounded-full bg-indigo-50 text-indigo-700 px-2 py-0.5 text-[11.5px] font-semibold" data-testid="jcr-history-panel-count">{plural(items.length)}</span>
      </SheetHeader>
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2.5 bg-slate-50/60" data-testid="jcr-history-panel-list">
        {items.map((c) => {
          const s = STATUS[c.status] || STATUS.pending;
          return (
            <button key={c.id} type="button" onClick={() => onView(c)}
              className="w-full text-left rounded-xl border border-slate-200 bg-white px-3.5 py-3 hover:border-indigo-300 hover:shadow-sm transition-[border-color,box-shadow] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
              data-testid={`jcr-history-item-${c.id.slice(0, 8)}`}>
              <div className="flex items-start gap-2.5">
                <s.Icon className={`h-4 w-4 mt-0.5 shrink-0 ${s.icon}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-[13px] text-slate-900 tracking-tight">{CORRECTION_ID(c.id)}</span>
                    <span className={`ml-auto shrink-0 rounded-full px-2 py-px text-[10.5px] font-semibold ring-1 ring-inset ${s.pill}`} data-testid={`jcr-history-item-status-${c.id.slice(0, 8)}`}>{s.label}</span>
                  </div>
                  <p className="text-[12px] text-slate-500 mt-0.5">{formatDateTimeET(c.submitted_at)}</p>
                  <p className="text-[12.5px] text-slate-700 mt-1 truncate" title={c.reason || ''}>{correctionSummary(c)}</p>
                  {c.status === 'rejected' && c.rejection_reason && <p className="text-[11.5px] text-rose-700 mt-0.5 line-clamp-2" title={c.rejection_reason}>{c.rejection_reason}</p>}
                  <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-indigo-700 mt-1.5">View request details <ChevronRight className="h-3.5 w-3.5" /></span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </SheetContent>
  </Sheet>
);

const PREVIEW_ROWS = 4;

/** Collapsible "Correction History" block rendered inside a jurisdiction rail card. */
export const CorrectionHistorySection = ({ items, jurisdiction, orderData, index }) => {
  const pending = useMemo(() => (items || []).filter((i) => i.status === 'pending').length, [items]);
  const [open, setOpen] = useState(pending > 0);
  const [panel, setPanel] = useState(false);
  const [view, setView] = useState(null);
  const [resubmit, setResubmit] = useState(null);
  useEffect(() => { if (pending > 0) setOpen(true); }, [pending]);
  if (!items?.length) return null;
  const stop = (e) => e.stopPropagation();
  const tid = `jcr-history-${index}`;
  return (
    <div className="mt-1 pl-5 pr-0.5" onClick={stop} onKeyDown={stop} data-testid={tid}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className="w-full flex flex-wrap items-center gap-x-1.5 gap-y-0.5 rounded-md py-0.5 text-[12px] font-semibold text-indigo-700 hover:text-indigo-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
        data-testid={`${tid}-toggle`}>
        <History className="h-3.5 w-3.5 shrink-0" />
        <span className="whitespace-nowrap">Correction History</span>
        <span className="ml-auto inline-flex items-center gap-1 shrink-0">
          {pending > 0 && <span className="rounded-full bg-amber-100 text-amber-800 px-1.5 py-px text-[10.5px] font-semibold ring-1 ring-inset ring-amber-200" data-testid={`${tid}-pending`}>{pending} pending</span>}
          <span className="rounded-full bg-indigo-50 text-indigo-700 px-1.5 py-px text-[10.5px] font-semibold" data-testid={`${tid}-count`}>{plural(items.length)}</span>
          <ChevronDown className={`h-3.5 w-3.5 text-indigo-400 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>
      {open && (
        <div className="mt-1 space-y-1" data-testid={`${tid}-list`}>
          {items.slice(0, PREVIEW_ROWS).map((c) => {
            const s = STATUS[c.status] || STATUS.pending;
            return (
              <button key={c.id} type="button" onClick={() => setView(c)}
                className="w-full flex items-center gap-1.5 rounded-md py-px text-left text-[11.5px] text-slate-700 hover:text-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
                title={correctionSummary(c)} data-testid={`${tid}-row-${c.id.slice(0, 8)}`}>
                <s.Icon className={`h-3.5 w-3.5 shrink-0 ${s.icon}`} />
                <span className="truncate">{formatDateOnlyET(c.submitted_at)} – {s.label}</span>
              </button>
            );
          })}
          <button type="button" onClick={() => setPanel(true)}
            className="inline-flex items-center gap-1 rounded-md py-0.5 text-[11.5px] font-semibold text-indigo-700 hover:text-indigo-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
            data-testid={`${tid}-view-all`}>
            View All Requests <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      <CorrectionHistoryPanel open={panel} onClose={() => setPanel(false)} items={items} jurisdiction={jurisdiction} onView={setView} />
      {view && <CorrectionDetailsLiteDialog request={view} onClose={() => setView(null)} onCorrect={(rej) => { setView(null); setPanel(false); setResubmit(rej); }} />}
      {resubmit && <RequestJurisdictionCorrectionDialog open onClose={() => setResubmit(null)} orderData={orderData} jurisdictionId={resubmit.jurisdiction_id} revisionOf={resubmit} />}
    </div>
  );
};

export default CorrectionHistorySection;
